import { action, computed, makeObservable, observable } from 'mobx';

import { getLiveEvents, type LiveEvent, type LiveEventsResponse } from 'api/events/endpoints/getLiveEvents';
import { answerOf, MobxQuery, type QueryValue } from 'mobx/MobxQuery';

import { LiveEventCardModel } from './LiveEventCardModel';
import { acceptDegraded } from 'validation';

/** The page-level filters, and the one URL-driven parameter. */
export interface LiveEventsParams {
  readonly zoneId: number | null;
  readonly search: string;
  readonly includeAutoSubmit: boolean;
  readonly focusEventId: number | null;
}

/**
 * Behaviour for the Live Events page.
 *
 * The page read is a {@link MobxQuery}: one derived value, re-asked whenever a
 * filter moves. It is the single source of truth for which events are listed
 * and what they currently look like; a {@link LiveEventCardModel} keeps no copy
 * and derives its event from here, holding only the answer to its own refresh.
 *
 * That leaves nothing to reconcile, so the whole model is derivations: card
 * membership is re-evaluated only by a page-level reload, not because anything
 * enforces it, but because a raise or per-card refresh calls the endpoint
 * directly and never touches `query.value`.
 *
 * Page-wide state stays here as the single source of truth — collapse and focus
 * are not copied onto the cards; each card derives its own boolean through its
 * back-pointer.
 */
export class LiveEventsModel {
  /**
   * Card instances by event id, rebuilt by {@link _unorderedCards} on each
   * evaluation so it drops cards that have left the page and cannot grow.
   *
   * Not observable and not a `createTransformer`: a transformer disposes on
   * de-observation, which would drop a card's own read the moment it stopped
   * being rendered. Nothing here needs invalidating either, because a card
   * holds no copy of its event — it derives one — so the only observable in
   * play is `query.value`.
   */
  private _cards = new Map<number, LiveEventCardModel>();
  /** Event ids whose collapse state is flipped relative to {@link _expandedByDefault}. */
  private _toggled: ReadonlySet<number> = new Set();
  private _expandedByDefault = true;
  private _focusEventId: number | null = null;
  private _includeAutoSubmit = false;
  private _search = '';
  private _zoneId: number | null = null;

  readonly query: MobxQuery<LiveEventsParams, LiveEventsResponse>;

  constructor() {
    makeObservable<
      LiveEventsModel,
      '_toggled' | '_expandedByDefault' | '_focusEventId' | '_includeAutoSubmit' | '_search' | '_zoneId' | '_eventsById' | '_unorderedCards'
    >(this, {
      _toggled: observable.ref,
      _expandedByDefault: observable,
      _focusEventId: observable,
      _includeAutoSubmit: observable,
      _search: observable,
      _zoneId: observable,
      cards: computed,
      _eventsById: computed,
      _unorderedCards: computed,
      collapseAll: action,
      expandAll: action,
      setFocusEventId: action,
      setIncludeAutoSubmit: action,
      setSearch: action,
      setZoneId: action,
      toggleCollapsed: action,
    });

    this.query = new MobxQuery<LiveEventsParams, LiveEventsResponse>({
      params: () => ({
        zoneId: this._zoneId,
        search: this._search,
        includeAutoSubmit: this._includeAutoSubmit,
        focusEventId: this._focusEventId,
      }),
      fetch: async (params) =>
        acceptDegraded(
          await getLiveEvents({
            ...(params.zoneId === null ? {} : { zone_id: params.zoneId }),
            search: params.search,
            include_auto_submit: params.includeAutoSubmit,
            // The focused event is force-included so its card appears even when
            // it is balanced — "surplus 0, demand 0" answers "why is nothing
            // happening?", where an empty page does not.
            ...(params.focusEventId === null ? {} : { event_ids: [params.focusEventId] }),
          })
        ),
      // Search is keystroke-shaped; the zone and auto-submit controls are
      // discrete but share the same params object, so they pay the same delay.
      // Everything goes `stale` immediately, so the page never looks fresh when
      // it is not.
      debounceMs: 300,
    });
  }

  // ── Accessors ─────────────────────────────────────────────────────────────

  /**
   * The cards to render, in display order: the focused one first, then the
   * page's own order. A card retained after a raise keeps its position — the
   * operator is looking straight at it — while a focused card is pinned,
   * because that is the one they navigated here to see.
   *
   * Split from {@link _unorderedCards} so that focus only ever reorders. The
   * build step does not read `_focusEventId`, so it cannot be invalidated by
   * focus moving.
   */
  get cards(): readonly LiveEventCardModel[] {
    const cards = this._unorderedCards;
    const focusIndex = cards.findIndex((card) => card.focused);
    if (focusIndex <= 0) {
      return cards;
    }
    return [cards[focusIndex], ...cards.slice(0, focusIndex), ...cards.slice(focusIndex + 1)];
  }

  get focusEventId(): number | null {
    return this._focusEventId;
  }

  get includeAutoSubmit(): boolean {
    return this._includeAutoSubmit;
  }

  /** True before the first answer lands; a spinner rather than an empty page. */
  get isLoading(): boolean {
    return this.query.value.kind === 'loading';
  }

  /** True while a newer answer is on its way; the cards on screen are dimmed, not blanked. */
  get isStale(): boolean {
    return this.query.value.kind === 'stale';
  }

  get pageError(): string | null {
    const value: QueryValue<LiveEventsResponse> = this.query.value;
    return value.kind === 'error' ? value.error.message : null;
  }

  get search(): string {
    return this._search;
  }

  /** The row cap was hit — the page must say so rather than imply completeness. */
  get truncated(): boolean {
    return answerOf(this.query.value)?.value.truncated ?? false;
  }

  get zoneId(): number | null {
    return this._zoneId;
  }

  /**
   * The page's events by id, as the last answer reported them.
   *
   * A computed rather than a plain map so that a card reading through
   * {@link eventById} takes a proper dependency on the answer and re-renders
   * when the page reloads.
   */
  private get _eventsById(): ReadonlyMap<number, LiveEvent> {
    const answer = answerOf(this.query.value);
    return new Map((answer?.value.events ?? []).map((event) => [event.event_id, event]));
  }

  /**
   * The page's cards in the backend's order, before focus is pinned.
   *
   * Instances are stable for as long as the event stays on the page, so one
   * card's raise re-renders that card alone and a page reload replaces what a
   * card shows rather than the card itself.
   *
   * Nothing is written to a card here, and nothing about a card is read. A
   * card derives its event through {@link eventById}, so there is no state to
   * reconcile and no reason for this computed to do anything but build the
   * list — which is what keeps it a computed rather than a disguised action.
   */
  private get _unorderedCards(): readonly LiveEventCardModel[] {
    const next = new Map<number, LiveEventCardModel>();

    const cards = [...this._eventsById.keys()].map((eventId) => {
      const card = this._cards.get(eventId) ?? new LiveEventCardModel(this, eventId);
      next.set(eventId, card);
      return card;
    });

    this._cards = next;
    return cards;
  }

  // ── Methods ───────────────────────────────────────────────────────────────

  /** The page's own view of one event, if it is still listed. */
  eventById = (eventId: number): LiveEvent | undefined => this._eventsById.get(eventId);

  collapseAll = (): void => {
    this._expandedByDefault = false;
    this._toggled = new Set();
  };

  dispose = (): void => {
    this.query.dispose();
  };

  expandAll = (): void => {
    this._expandedByDefault = true;
    this._toggled = new Set();
  };

  /**
   * Whether one card is collapsed — the single source of truth for the page.
   *
   * Expand-all / collapse-all is a flag flip plus an empty toggle set, so it
   * applies to cards that have never been rendered.
   */
  isCollapsed = (eventId: number): boolean => (this._expandedByDefault ? this._toggled.has(eventId) : !this._toggled.has(eventId));

  /**
   * Re-evaluate the whole page. The only thing that adds or removes cards —
   * it produces a new answer, which rebuilds them.
   */
  refreshPage = async (): Promise<void> => {
    await this.query.refresh();
  };

  setFocusEventId = (eventId: number | null): void => {
    this._focusEventId = eventId;
  };

  setIncludeAutoSubmit = (include: boolean): void => {
    this._includeAutoSubmit = include;
  };

  setSearch = (search: string): void => {
    this._search = search;
  };

  setZoneId = (zoneId: number | null): void => {
    this._zoneId = zoneId;
  };

  toggleCollapsed = (eventId: number): void => {
    const next = new Set(this._toggled);
    if (next.has(eventId)) {
      next.delete(eventId);
    } else {
      next.add(eventId);
    }
    this._toggled = next;
  };
}
